import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { PageType } from '@/components/entities/requests/model/types'
import { resolveBasePath } from '@/utils/deepLinkRouter'

const deleteSearchParams = (params: URLSearchParams, keys: string[]) => {
  keys.forEach(key => params.delete(key))
}

const REQUEST_ID_PARAMS = ['requestId', 'passId']
const BOOKING_UNPAID_PARAM = 'unpaid'
const TRANSIENT_DRAWER_PARAMS = [
  'repeat',
  'isSuccess',
  'comments',
  'editing',
  'autoId'
]
const BOOKING_PAYMENT_PARAMS = ['bookingId', 'invoiceId', 'personalAccountId']

interface UseRequestNavigationReturn {
  navigateToCreateFormRequest: () => void
  navigateToRequestDetailsSuccess: (
    pageType: PageType,
    requestId: string
  ) => void
  navigateToCloseDetails: () => Promise<void>
  navigateToRepeatRequest: (
    pageType: PageType,
    id: string,
    typeAlias: RequestTypeAlias
  ) => void
  navigateToContentsFromDetails: (pageType: PageType, id: string) => void
  navigateToCommentsFromDetails: (pageType: PageType, requestId: string) => void
  navigateToAvailablePasses: () => void
  navigateToCreateFormPass: (passType: RequestTypeAlias) => void
  navigateToPassDetailsSuccess: (passId: string) => void
  navigateToPaymentFromDetails: () => void
  navigateToPaymentFromBooking: (params: {
    bookingId: string
    invoiceId: string
    personalAccountId: string
  }) => void
  navigateFromPayment: () => void
  navigateToBookingUnpaid: () => void
  navigateBackFromBookingUnpaid: () => void
  navigateToCreateRequestFromCompletedChat: () => void
  shouldShowComments: boolean
  shouldShowPayment: boolean
  shouldShowBookingUnpaid: boolean
}

/**
 * Хук для навигации по маршрутам пропусков и обращений
 * Предоставляет методы для отображения данных в RequestDrawer
 */
export const useRequestNavigation = (): UseRequestNavigationReturn => {
  const router = useRouter()

  const searchParams = useSearchParams()

  /**
   * открытие формы создания обращения
   */
  const navigateToCreateFormRequest = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    const pathname = PageType.Requests
    params.set('type', RequestTypeAlias.CommonRequest)
    router.push(`${pathname}?${params}`)
  }, [router, searchParams])

  /**
   * переход в детальную карточку обращения после успешного создания обращения
   */
  const navigateToRequestDetailsSuccess = useCallback(
    (pageType: string, id: string) => {
      const paramName = pageType === PageType.Requests ? 'requestId' : 'passId'
      const params = new URLSearchParams(searchParams?.toString())
      params.delete('type')
      params.delete('repeat')
      params.set(paramName, id)
      router.push(`${router.pathname}?${params}&isSuccess`)
    },
    [router, searchParams]
  )

  /**
   * закрытие детальной карточки
   */

  // TODO: add params new URLSearchParams request/passes in Enum
  const navigateToCloseDetails = useCallback(async () => {
    const params = new URLSearchParams(searchParams?.toString())
    deleteSearchParams(params, [
      'type',
      'new',
      ...TRANSIENT_DRAWER_PARAMS,
      ...REQUEST_ID_PARAMS,
      ...BOOKING_PAYMENT_PARAMS,
      'step',
      'payment',
      BOOKING_UNPAID_PARAM
    ])

    const queryString = params.toString()

    if (resolveBasePath(router.pathname)) {
      const basePath = resolveBasePath(router.pathname)
      const url = queryString ? `${basePath}?${queryString}` : basePath
      await router.push(url)
    } else {
      const basePath = router.asPath.split('?')[0]
      const url = queryString ? `${basePath}?${queryString}` : basePath
      await router.push(url)
    }
  }, [router, searchParams])

  /**
   * повторение пропуска/обращения из детальной карточки
   */
  const navigateToRepeatRequest = useCallback(
    (pageType: string, id: string, typeAlias: RequestTypeAlias) => {
      const params = new URLSearchParams(searchParams?.toString())
      const paramName = pageType === PageType.Requests ? 'requestId' : 'passId'
      params.delete(paramName)
      params.set('type', typeAlias)
      params.set('repeat', id)

      const basePath = resolveBasePath(router.pathname)
      router.push(`${basePath}?${params}`)
    },
    [router, searchParams]
  )

  /**
   * переход в контент детальной карточки
   */
  const navigateToContentsFromDetails = useCallback(
    (pageType: string, id: string) => {
      const isCommentsRoute = router.pathname.includes('/comments')

      let pathname = resolveBasePath(router.pathname)
      // Для корректной работы с диплинками:
      // Если текущий маршрут содержит /comments (например, /requests/[requestId]/comments),
      // удаляем его, чтобы открывать дровер с дефолтным контентом
      if (isCommentsRoute) {
        pathname = pathname.replace('/comments', '')
      }

      const params = new URLSearchParams(searchParams?.toString())
      const paramName = pageType === PageType.Requests ? 'requestId' : 'passId'

      params.delete(paramName)
      deleteSearchParams(params, ['comments', 'payment', 'repeat', 'isSuccess'])
      params.set(paramName, id)

      const queryString = params.toString()
      const url = queryString ? `${pathname}?${queryString}` : pathname
      router.push(url)
    },
    [router, searchParams]
  )

  /**
   * переход в комментарии из детальной карточки пропуска/обращения
   */
  const navigateToCommentsFromDetails = useCallback(
    (pageType: string, requestId: string) => {
      const params = new URLSearchParams(searchParams?.toString())
      const paramName = pageType === PageType.Requests ? 'requestId' : 'passId'

      params.delete(paramName)
      params.set(paramName, requestId)
      params.set('comments', '')

      router.push(`${router.pathname}?${params}`)
    },
    [router, searchParams]
  )

  /**
   * Открытие списка доступных пропусков
   */
  const navigateToAvailablePasses = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete('type')
    params.set('new', 'new')
    router.push(`${router.pathname}?${params}`)
  }, [router, searchParams])

  /**
   * открытие формы создание пропуска по типу
   */
  const navigateToCreateFormPass = useCallback(
    (passType: RequestTypeAlias) => {
      const params = new URLSearchParams(searchParams?.toString())
      const typeParam =
        passType === RequestTypeAlias.PermanentPass
          ? RequestTypeAlias.PermanentPassFamily
          : passType
      params.set('type', typeParam)
      router.push(`${router.pathname}?${params}`)
    },
    [router, searchParams]
  )

  /**
   * переход в детальную карточку обращения после успешного создания пропуска
   */
  const navigateToPassDetailsSuccess = useCallback(
    (passId: string) => {
      const params = new URLSearchParams(searchParams?.toString())
      deleteSearchParams(params, [
        'type',
        'new',
        'repeat',
        ...REQUEST_ID_PARAMS
      ])
      params.set('passId', passId)
      router.push(`${router.pathname}?${params}&isSuccess`)
    },
    [router, searchParams]
  )

  /**
   * открытие формы оплаты из детальной карточки обращения
   */
  const navigateToPaymentFromDetails = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    const basePath = resolveBasePath(router.pathname)

    deleteSearchParams(params, [...TRANSIENT_DRAWER_PARAMS, 'step'])

    if (basePath !== router.pathname) {
      const { requestId, passId } = router.query

      if (typeof requestId === 'string') {
        params.set('requestId', requestId)
      }

      if (typeof passId === 'string') {
        params.set('passId', passId)
      }
    }

    params.set('payment', '')
    router.push(`${basePath}?${params}`)
  }, [router, searchParams])

  const navigateToPaymentFromBooking = useCallback(
    ({
      bookingId,
      invoiceId,
      personalAccountId
    }: {
      bookingId: string
      invoiceId: string
      personalAccountId: string
    }) => {
      const params = new URLSearchParams(searchParams?.toString())
      const basePath = resolveBasePath(router.pathname)

      deleteSearchParams(params, [
        'type',
        'new',
        ...TRANSIENT_DRAWER_PARAMS,
        'step',
        ...REQUEST_ID_PARAMS
      ])

      params.set('bookingId', bookingId)
      params.set('invoiceId', invoiceId)
      params.set('personalAccountId', personalAccountId)
      params.set('payment', '')

      router.push(`${basePath}?${params}`)
    },
    [router, searchParams]
  )

  /**
   * закрытие формы оплаты
   */
  const navigateFromPayment = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete('payment')
    deleteSearchParams(params, [
      ...BOOKING_PAYMENT_PARAMS,
      BOOKING_UNPAID_PARAM
    ])
    const queryString = params.toString()
    const basePath = resolveBasePath(router.pathname)
    const url = queryString ? `${basePath}?${queryString}` : basePath
    router.push(url)
  }, [router, searchParams])

  const navigateToBookingUnpaid = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    const basePath = resolveBasePath(router.pathname)

    params.set(BOOKING_UNPAID_PARAM, '')

    router.push(`${basePath}?${params}`)
  }, [router, searchParams])

  const navigateBackFromBookingUnpaid = useCallback(() => {
    router.back()
  }, [router])

  /**
   * Переход на форму создания нового обращения из чата
   * выполненного обращения. Перед переходом очищает URL
   * от параметров открытой карточки.
   */

  const navigateToCreateRequestFromCompletedChat = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    const basePath = resolveBasePath(router.pathname) || PageType.Requests

    deleteSearchParams(params, ['requestId', 'passId', 'comments'])
    params.set('type', RequestTypeAlias.CommonRequest)

    router.push(`${basePath}?${params}`)
  }, [router, searchParams])

  /**
   * определяет, показывать ли чат с комментарием в детальной карточке
   */
  const shouldShowComments = useMemo(
    () => router.asPath.includes('comments'),
    [router.asPath]
  )

  /**
   * определяет, показывать ли форму оплаты в детальной карточке
   */
  const shouldShowPayment = useMemo(
    () => searchParams?.has('payment') ?? false,
    [searchParams]
  )

  /**
   * определяет, показывать ли шторку "Аренда не оплачена"
   * Показывается при закрытии крестиком экрана оплаты пропуска на аренду,
   * так как к этому моменту бронирование уже создано и данные не теряются.
   * Хранится в URL, чтобы кнопка "Назад" (в том числе браузерная)
   * возвращала на экран оплаты
   */
  const shouldShowBookingUnpaid = useMemo(
    () => searchParams?.has(BOOKING_UNPAID_PARAM) ?? false,
    [searchParams]
  )

  return {
    navigateToCreateFormRequest,
    navigateToRequestDetailsSuccess,
    navigateToCloseDetails,
    navigateToContentsFromDetails,
    navigateToRepeatRequest,
    navigateToCommentsFromDetails,
    navigateToAvailablePasses,
    navigateToCreateFormPass,
    navigateToPassDetailsSuccess,
    navigateToPaymentFromDetails,
    navigateToPaymentFromBooking,
    navigateFromPayment,
    navigateToBookingUnpaid,
    navigateBackFromBookingUnpaid,
    navigateToCreateRequestFromCompletedChat,
    shouldShowComments,
    shouldShowPayment,
    shouldShowBookingUnpaid
  }
}
